// src/components/Dashboard.js
import React, { useEffect, useState } from 'react';
import { formatCurrency } from '../utils/currency';
import BillableHoursCard from './BillableHoursCard';

const EM_DASH = '—';

// Static Tailwind class maps — dynamic interpolation is purged in production builds.
const BORDER_COLOR = {
  blue:   'border-blue-500',
  green:  'border-green-500',
  yellow: 'border-yellow-500',
  red:    'border-red-500',
  teal:   'border-teal-500',
  purple: 'border-purple-500',
};
const LABEL_COLOR = {
  blue:   'text-blue-600',
  green:  'text-green-600',
  yellow: 'text-yellow-600',
  red:    'text-red-600',
  teal:   'text-teal-600',
  purple: 'text-purple-600',
};
const BADGE_CLASS = {
  green: 'bg-green-100 text-green-700',
  red:   'bg-red-100 text-red-700',
  gray:  'bg-gray-100 text-gray-600',
};

function fmt(value, suffix = '') {
  if (value === null || value === undefined || value === '') return EM_DASH;
  const num = parseFloat(value);
  if (Number.isNaN(num)) return EM_DASH;
  return `${num}${suffix}`;
}

function KpiCard({ label, value, color, sub, badge, badgeTone }) {
  const isEmpty = value === EM_DASH;
  return (
    <div className={`bg-white rounded-lg shadow-sm border-l-4 ${BORDER_COLOR[color] || 'border-gray-400'} p-4 flex flex-col gap-1`}>
      <p className={`text-xs font-semibold uppercase tracking-wide ${LABEL_COLOR[color] || 'text-gray-500'}`}>
        {label}
      </p>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-bold ${isEmpty ? 'text-gray-300' : 'text-gray-800'}`}>
          {value}
        </span>
        {badge && (
          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${BADGE_CLASS[badgeTone] || BADGE_CLASS.gray}`}>
            {badge}
          </span>
        )}
      </div>
      {sub && <p className="text-xs text-gray-500 truncate">{sub}</p>}
    </div>
  );
}

function Dashboard({ currentUser, authAxios, showFormMessage, recentActivity = [], handleActivityClick, setActiveTab, setNavigateTo }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authAxios) return;
    authAxios.get('dashboard/metrics/')
      .then(res => setMetrics(res.data))
      .catch(() => showFormMessage && showFormMessage('Failed to load dashboard metrics.', 'error'))
      .finally(() => setLoading(false));
  }, [authAxios, showFormMessage]);

  const buildCards = (m) => {
    const growth = parseFloat(m.client_growth_pct);
    const hasGrowth = !Number.isNaN(growth);
    const growthTone = !hasGrowth ? 'gray' : growth > 0 ? 'green' : growth < 0 ? 'red' : 'gray';

    return [
      {
        label: 'Total Clients',
        value: m.total_clients ?? EM_DASH,
        color: 'blue',
        sub: 'vs. previous month.',
        badge: hasGrowth ? `${growth > 0 ? '+' : ''}${growth}%` : null,
        badgeTone: growthTone,
      },
      {
        label: 'Open Cases',
        value: m.open_cases ?? EM_DASH,
        color: 'green',
        sub: 'Active legal matters.',
      },
      {
        label: 'Upcoming Tasks',
        value: m.upcoming_tasks ?? EM_DASH,
        color: 'yellow',
        sub: 'Due within 7 days.',
      },
      {
        label: 'Overdue Invoices',
        value: m.overdue_invoices ?? EM_DASH,
        color: 'red',
        sub: m.overdue_invoices_amount && parseFloat(m.overdue_invoices_amount) > 0
          ? `Outstanding: ${formatCurrency(m.overdue_invoices_amount)}`
          : 'No outstanding balance.',
      },
      {
        label: 'Bills Due / Overdue',
        value: m.overdue_bills ?? EM_DASH,
        color: 'yellow',
        sub: m.overdue_bills_amount && parseFloat(m.overdue_bills_amount) > 0
          ? `Payable: ${formatCurrency(m.overdue_bills_amount)}`
          : 'No bills overdue.',
      },
      {
        label: 'Debt Recovery',
        value: fmt(m.debt_recovery_rate, '%'),
        color: 'teal',
        sub: 'Recovered ÷ total claimed.',
      },
      {
        label: 'Payment Rate',
        value: fmt(m.invoice_payment_rate, '%'),
        color: 'purple',
        sub: 'Invoices fully paid.',
      },
    ];
  };

  const cards = metrics ? buildCards(metrics) : [];

  // Navigate to a module and signal it to open its add form.
  const quickAction = (tab) => {
    if (!setActiveTab || !setNavigateTo) return;
    setActiveTab(tab);
    setNavigateTo({ tab, type: 'add_new', autoOpen: true });
  };

  const quickActions = [
    { label: 'Add New Client',      tab: 'clients' },
    { label: 'Create New Case',     tab: 'cases' },
    { label: 'Log Time Entry',      tab: 'timeExpense' },
    { label: 'Add Communication',   tab: 'communicationLogs' },
  ];

  return (
    <div className="p-6 space-y-8">

      {/* KPI Row — 1-col mobile / 2-col tablet / 3-col desktop */}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((card) => (
            <KpiCard key={card.label} {...card} />
          ))}
        </div>
      )}

      {/* Billable Hours (2/3) + Recent Activity (1/3) — uniform height */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {/* BillableHoursCard has col-span-2 via its own className; the 3-col
            grid makes it occupy 2/3 width on desktop. */}
        <BillableHoursCard authAxios={authAxios} />
        <div className="dashboard-widget flex flex-col">
          <h3 className="widget-title shrink-0">Recent Activity</h3>
          {/* max-h-[400px] applies at every breakpoint (not just below lg:) so a
              long activity list can never grow taller than Billable Hours' own
              content and drag the grid row (and Billable Hours) up with it —
              items-stretch/`.dashboard-widget{height:100%}` only stretch items
              to fill whatever row height results, they don't cap one item from
              *setting* that height in the first place. Once capped, the list
              scrolls internally instead. */}
          <div className="overflow-y-auto flex-1 min-h-0 max-h-[400px] recent-activity-card">
            {recentActivity.length === 0 ? (
              <p className="p-4 text-sm text-gray-400">No recent activity.</p>
            ) : (
              <ul className="p-4 space-y-2">
                {recentActivity.slice(0, 10).map((activity, index) => (
                  <li
                    key={`${activity.type}-${activity.id}-${index}`}
                    className="activity-item"
                    onClick={() => handleActivityClick && handleActivityClick(activity)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-800 text-sm truncate">{activity.title}</p>
                      <p className="text-xs text-gray-500 truncate">{activity.description}</p>
                    </div>
                    <span className="ml-2 text-xs text-gray-400 whitespace-nowrap">
                      {activity.due_date
                        ? new Date(activity.due_date).toLocaleDateString([], { month: 'short', day: 'numeric' })
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h3 className="text-lg font-semibold text-gray-700 mb-3">Quick Actions</h3>
        <div className="flex flex-wrap gap-3">
          {quickActions.map(({ label, tab }) => (
            <button
              key={tab}
              onClick={() => quickAction(tab)}
              className="bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white font-medium py-2 px-4 rounded-md shadow-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

    </div>
  );
}

export default Dashboard;
